import type {
  SourceUsageChartPoint,
  SourceUsageChartSeriesItem,
} from "../../../components/OverviewSourceUsage/types";
import { formatSourceUsageDate } from "../../../components/OverviewSourceUsage/utils/formatSourceUsageDate";
import type { OverviewDomainsAggRow } from "../../../store/overview/types";

const CHART_SERIES_COLORS = [
  "var(--aicarma-color-fuchsia)",
  "var(--aicarma-color-gray)",
  "var(--aicarma-color-red)",
  "var(--aicarma-color-blue2)",
  "var(--aicarma-color-green)",
  "var(--aicarma-color-yellow3)",
  "var(--aicarma-color-dark3)",
  "var(--aicarma-color-green2)",
];

const MAX_SERIES = 5;

export interface NormalizedOverviewSourceUsageChart {
  data: SourceUsageChartPoint[];
  series: SourceUsageChartSeriesItem[];
}

/**
 * Normalizes raw overview domains into chart data and series.
 * One point per date; each point has usage percent per source (top domains by total links).
 * @param rows - Raw overview domains from API.
 * @param dateRange - Optional list of "yyyy-MM-dd" dates. When set, chart shows this full range;
 *   days without data get a point with zeros (empty).
 */
export function normalizeOverviewSourceUsageChart(
  rows: OverviewDomainsAggRow[],
  dateRange?: string[]
): NormalizedOverviewSourceUsageChart {
  const dates: string[] =
    dateRange && dateRange.length > 0
      ? dateRange
      : [...new Set(rows.map((r) => r.response_date))].sort();

  if (dates.length === 0) {
    return { data: [], series: [] };
  }

  const totalByDomain = new Map<
    number,
    { domain: string; links_count: number }
  >();
  for (const r of rows) {
    const cur = totalByDomain.get(r.domain_id);
    if (cur) {
      cur.links_count += r.links_count;
    } else {
      totalByDomain.set(r.domain_id, {
        domain: r.domain,
        links_count: r.links_count,
      });
    }
  }

  const sortedDomains = [...totalByDomain.entries()]
    .sort((a, b) => b[1].links_count - a[1].links_count)
    .slice(0, MAX_SERIES);

  const data: SourceUsageChartPoint[] = dates.map((responseDate) => {
    const dayRows = rows.filter((r) => r.response_date === responseDate);
    const dayTotal = dayRows.reduce((s, r) => s + r.links_count, 0);
    const hasDataForDay = dayTotal > 0;
    const point: SourceUsageChartPoint = {
      date: formatSourceUsageDate(responseDate),
    };
    for (const [domainId] of sortedDomains) {
      if (!hasDataForDay) {
        point[`domain_${domainId}`] = null;
      } else {
        const row = dayRows.find((r) => r.domain_id === domainId);
        const value = row
          ? (row.links_count / dayTotal) * 100
          : 0;
        point[`domain_${domainId}`] = Math.round(value * 10) / 10;
      }
    }
    return point;
  });

  const series: SourceUsageChartSeriesItem[] = sortedDomains.map(
    ([domainId, { domain }], i) => ({
      dataKey: `domain_${domainId}`,
      name: domain,
      color: CHART_SERIES_COLORS[i % CHART_SERIES_COLORS.length],
    })
  );

  return { data, series };
}
